import { useState } from "react";
import { Loader2, Plug } from "lucide-react";
import type { OAuthGrant } from "@supabase/supabase-js";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useOAuthGrants, useRevokeOAuthGrant } from "@/hooks/useOAuthGrants";
import { formatRelative } from "@/lib/format";
import { clientLabel, shortClientId, uriHost } from "@/lib/oauth";
import { cn } from "@/lib/utils";

/**
 * Settings > Connected apps: the hosts this user granted on the consent
 * page, with a revoke per host (ADR-058).
 *
 * A grant carries only what the client declared about itself — no
 * redirect URI — so rows are told apart by the authorization date and a
 * short client id. The client's `logo_uri` is never rendered: it is an
 * arbitrary third-party URL chosen by whoever registered the client.
 */
export function ConnectedApps() {
  const { data: grants, isLoading, error } = useOAuthGrants();
  const revoke = useRevokeOAuthGrant();
  const [toRevoke, setToRevoke] = useState<OAuthGrant | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-4" data-testid="connected-apps-loading">
        {[...Array(2)].map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-destructive">
        Error loading connected apps: {error.message}
      </div>
    );
  }

  if (!grants || grants.length === 0) {
    return (
      <Card data-testid="connected-apps-empty">
        <CardContent className="flex flex-col items-center justify-center py-12 text-center gap-4">
          <div className="rounded-full bg-muted p-3">
            <Plug className="size-6 text-muted-foreground" />
          </div>
          <div className="flex flex-col gap-1">
            <h3 className="font-semibold">No connected apps</h3>
            <p className="text-sm text-muted-foreground max-w-sm">
              When you connect an AI assistant such as Claude to Kopai, it
              appears here and you can revoke its access at any time.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const revokeName = toRevoke ? clientLabel(toRevoke.client.name) : "";

  return (
    <div className="space-y-4" data-testid="connected-apps">
      {grants.map((grant) => {
        const name = clientLabel(grant.client.name);
        const website = uriHost(grant.client.uri);
        return (
          <Card key={grant.client.id} data-testid="connected-app">
            <CardContent className="flex items-center justify-between gap-4 py-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-muted">
                  <Plug className="h-5 w-5" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium break-words">{name}</div>
                  <div className="flex flex-wrap gap-x-3 text-sm text-muted-foreground">
                    <span title={new Date(grant.granted_at).toLocaleString()}>
                      Authorized {formatRelative(grant.granted_at)}
                    </span>
                    {website && (
                      <span>
                        Website (declared by the app):{" "}
                        <span className="font-mono">{website}</span>
                      </span>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="font-mono cursor-default">
                          ID {shortClientId(grant.client.id)}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{grant.client.id}</TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setToRevoke(grant);
                }}
              >
                Revoke
              </Button>
            </CardContent>
          </Card>
        );
      })}

      <AlertDialog
        open={toRevoke !== null}
        onOpenChange={(open) => {
          if (!open && !revoke.isPending) setToRevoke(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke access</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{revokeName}</strong> will no longer be able to read your
              workspace&apos;s telemetry; it loses access within 30 seconds. If
              you connected it from more than one place that shares this app —
              for example claude.ai and Claude Desktop — all of them lose
              access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoke.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={revoke.isPending}
              className={cn(buttonVariants({ variant: "destructive" }))}
              onClick={(e) => {
                e.preventDefault();
                if (!toRevoke) return;
                revoke.mutate(toRevoke.client.id, {
                  onSettled: () => {
                    setToRevoke(null);
                  },
                });
              }}
            >
              {revoke.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Revoking...
                </>
              ) : (
                "Revoke"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
